import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getActiveDisabledReasons, DisabledReason } from '../services/disabledReasonsService';
import { X, AlertTriangle } from 'lucide-react';
import './DeactivateFamilyModal.css';

interface DeactivateFamilyModalProps {
  familyName: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: DisabledReason) => void;
  isLoading?: boolean;
}

export default function DeactivateFamilyModal({
  familyName,
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: DeactivateFamilyModalProps) {
  const [selectedReasonId, setSelectedReasonId] = useState<string>('');
  const [customTitle, setCustomTitle] = useState('');
  const [customMessage, setCustomMessage] = useState('');

  const { data: reasons, isLoading: reasonsLoading } = useQuery({
    queryKey: ['activeDisabledReasons'],
    queryFn: getActiveDisabledReasons,
    enabled: isOpen,
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedReasonId('');
      setCustomTitle('');
      setCustomMessage('');
    } else if (reasons && reasons.length > 0) {
      // Auto-select first reason
      setSelectedReasonId(reasons[0].id);
      setCustomTitle(reasons[0].title);
      setCustomMessage(reasons[0].message);
    }
  }, [isOpen, reasons]);

  // Update custom fields when reason selection changes
  useEffect(() => {
    if (selectedReasonId && reasons) {
      const selected = reasons.find(r => r.id === selectedReasonId);
      if (selected) {
        setCustomTitle(selected.title);
        setCustomMessage(selected.message);
      }
    }
  }, [selectedReasonId, reasons]);

  if (!isOpen) return null;

  const selectedReason = reasons?.find(r => r.id === selectedReasonId);
  const canConfirm = selectedReasonId && customTitle.trim() && customMessage.trim();

  const handleConfirm = () => {
    if (selectedReason && canConfirm) {
      // Use custom title/message if modified, otherwise use reason defaults
      const reasonToUse: DisabledReason = {
        ...selectedReason,
        title: customTitle.trim(),
        message: customMessage.trim(),
      };
      onConfirm(reasonToUse);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="deactivate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <AlertTriangle className="warning-icon" size={24} />
            <h2>Deactivate Family Account</h2>
          </div>
          <button className="close-button" onClick={onClose} disabled={isLoading}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-content">
          <p className="warning-text">
            You are about to deactivate <strong>{familyName}</strong>. Please select a reason for deactivation.
          </p>

          {reasonsLoading ? (
            <div className="loading-state">Loading reasons...</div>
          ) : !reasons || reasons.length === 0 ? (
            <div className="error-state">
              <p>No disabled reasons available. Please create one in Settings → Disabled Reasons.</p>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="reason-select">Reason for Deactivation *</label>
                <select
                  id="reason-select"
                  value={selectedReasonId}
                  onChange={(e) => setSelectedReasonId(e.target.value)}
                  disabled={isLoading}
                >
                  {reasons.map((reason) => (
                    <option key={reason.id} value={reason.id}>
                      {reason.title}
                    </option>
                  ))}
                </select>
              </div>

              {selectedReason && (
                <>
                  <div className="form-group">
                    <label htmlFor="custom-title">Title *</label>
                    <input
                      id="custom-title"
                      type="text"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      placeholder="Enter title for this deactivation"
                      disabled={isLoading}
                    />
                    <small>You can customize the title shown to users</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="custom-message">Message *</label>
                    <textarea
                      id="custom-message"
                      value={customMessage}
                      onChange={(e) => setCustomMessage(e.target.value)}
                      placeholder="Enter message for this deactivation"
                      rows={4}
                      disabled={isLoading}
                    />
                    <small>This message will be shown to the family when they try to access their account</small>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          <button
            className="secondary-button"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="danger-button"
            onClick={handleConfirm}
            disabled={!canConfirm || isLoading}
          >
            {isLoading ? 'Deactivating...' : 'Deactivate Account'}
          </button>
        </div>
      </div>
    </div>
  );
}


